// Standard libs
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
// Custom libraries
import Header from "components/header";
import Footer from "components/footer";
import HeaderMarketingSpot from "components/headerMarketingSpot";
// Redux

// UI
import { ErrorAlert } from "components/error";

import CookieConsentModal from "components/cookieConsent/CookieConsentModal";
import { StyledScrollUpContainer } from "components/StyledUI";
import ScrollToTop from "components/helpers/ScrollToTop";
import MatomoProvider, {
  MatomoTagManager,
  TrackRoute,
} from "components/matomo";
import { Dialog } from "components/dialog";
import CentralLogonDialog from "components/CentralLogonDialog";
import OnlineStatusInformation from "pwa/onlineStatus/OnlineStatusInformation";
import OfflineSyncWatcher from "pwa/offline/sync/components/OfflineSyncWatcher";
import TriggerCartSyncWatcher from "pwa/offline/cart/TriggerCartSyncWatcher";
import OnlineStatusChecker from "pwa/onlineStatus/OnlineStatusChecker";
import ForceStandaloneModeWatcher from "pwa/displayMode/ForceStandaloneModeWatcher";
import { Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "_redux/hooks";
import useAxiosMaintenanceInterceptor from "_foundationExt/hooks/useAxiosMaintenanceInterceptor";
import { useAxiosInterceptor } from "_foundation/axios/axiosConfig";
import {
  initSite,
  useAbortControllers,
  useScript,
  useSite,
} from "_foundationExt/hooks";
import { useTranslation } from "react-i18next";
import { localStorageUtil } from "_foundationExt/utils/storageUtil";
import { LOCALE } from "_foundation/constants/common";
import { CommerceEnvironment } from "constants/common";
import { externalScripts } from "constants/scripts";
import { useScrollRestoration } from "tools/scrollRestoration";
import { INIT_STATE_FROM_STORAGE_ACTION } from "_redux/actions/user";
import { site } from "_foundationExt/constants/site";
import Home from "pages/home/Home";
import Lazy from "components/routes/Lazy";
import { PERMANENT_STORE_DAYS } from "configs/common";
import { supportedLanguagesSelector } from "_redux/selectors/site";
import {
  loginStatusSelector,
  preferredLanguageSelector,
} from "_redux/selectors/user";

const HeaderIncludes: FC = () => (
  <>
    <ForceStandaloneModeWatcher />
    <OnlineStatusChecker />
    <TriggerCartSyncWatcher />
    <ScrollToTop />
    <HeaderMarketingSpot />
    <MatomoTagManager />
    <Dialog />
    <CentralLogonDialog />
  </>
);

const BodyIncludes: FC = () => (
  <>
    <OnlineStatusInformation />
    <OfflineSyncWatcher />
    <ErrorAlert />
    <CookieConsentModal />
    <StyledScrollUpContainer />
  </>
);

const AppLayout: FC = () => {
  const dispatch = useAppDispatch();
  useAxiosMaintenanceInterceptor();
  useAxiosInterceptor();
  const { currentSite } = useSite();
  const { i18n } = useTranslation();
  const abortControllers = useAbortControllers();

  const preferredLanguage = useAppSelector(preferredLanguageSelector);
  const loggedIn = useAppSelector(loginStatusSelector);
  const supportedLanguages = useAppSelector(supportedLanguagesSelector);

  const setTranslate = useCallback(() => {
    if (!currentSite) return;

    // Get the stored locale from local storage
    const storedLocale = localStorageUtil.get(LOCALE);

    if (!storedLocale) {
      const browserLanguages = navigator.languages || [navigator.language];
      const favouriteBrowserLanguage = browserLanguages.find((lang) => {
        // Extract the two-letter language code (e.g., "de" from "de-DE")
        const shortLang = lang.slice(0, 2).toLowerCase();
        return (
          // Check if the language is supported by both the frontend and the store
          CommerceEnvironment.reverseLanguageMapShort[shortLang] &&
          supportedLanguages?.includes(
            CommerceEnvironment.reverseLanguageMapShort[shortLang]
          )
        );
      });

      // Use the preferred language if it is supported, otherwise use the store's default language
      const locale = favouriteBrowserLanguage
        ? favouriteBrowserLanguage.slice(0, 2).toLowerCase()
        : CommerceEnvironment.languageMapShort[currentSite.defaultLanguageID];

      if (locale !== i18n.language) {
        i18n.changeLanguage(locale);
      }
      // set locale into local storage
      localStorageUtil.set(LOCALE, locale, PERMANENT_STORE_DAYS);
    } else {
      i18n.changeLanguage(storedLocale);
    }
  }, [currentSite, i18n, supportedLanguages]);

  useScript(
    currentSite?.storeName ? externalScripts[currentSite.storeName] : null
  );

  useScrollRestoration();

  const preferredLocale = useMemo(() => {
    if (preferredLanguage) {
      return CommerceEnvironment.localeToLanguageMap[preferredLanguage];
    }
    return undefined;
  }, [preferredLanguage]);

  useLayoutEffect(() => {
    const shouldChangeLanguage =
      loggedIn && preferredLocale != null && preferredLocale !== i18n.language;

    if (shouldChangeLanguage) {
      i18n.changeLanguage(preferredLocale);
    }
  }, [loggedIn, preferredLocale, i18n]);

  useEffect(() => {
    if (currentSite) {
      dispatch(INIT_STATE_FROM_STORAGE_ACTION());
      setTranslate();
    } else {
      const { signal } = abortControllers.create();
      initSite(site, dispatch, signal);
    }
  }, [currentSite, dispatch, setTranslate, abortControllers]);

  return (
    <MatomoProvider>
      <HeaderIncludes />
      <Header />
      <main>
        <BodyIncludes />
        {currentSite?.storeID === "10101" ? (
          <Lazy>
            <Home />
          </Lazy>
        ) : (
          <Outlet />
        )}
        <TrackRoute />
      </main>
      <Footer />
    </MatomoProvider>
  );
};

export default AppLayout;
